import type { TaskDropdownCatalog, TaskDropdownDefinition } from '@techgitter/contracts';

const API_BASE_URL = '/api';

/** A dropdown row as returned by the API — carries the Dataverse row id. */
export interface TaskDropdownRow extends TaskDropdownDefinition {
  id?: string;
  isActive?: boolean;
}

/** Fetch the reusable task-dropdown option sets in `{schemaVersion, dropdowns}` shape. */
export async function getTaskDropdowns(
  signal?: AbortSignal,
): Promise<TaskDropdownCatalog> {
  const response = await fetch(`${API_BASE_URL}/task-dropdowns`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch task dropdowns: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}

/** Bulk-replace the catalog (admin GUI/JSON editor); diff-and-apply server-side. */
export async function putTaskDropdowns(
  catalog: TaskDropdownCatalog,
  signal?: AbortSignal,
): Promise<TaskDropdownCatalog> {
  const response = await fetch(`${API_BASE_URL}/task-dropdowns`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(catalog),
    signal,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to save task dropdowns: ${response.status}`,
    }));
    throw new Error(
      errorData.error || `Failed to save task dropdowns: ${response.status}`,
    );
  }
  return response.json();
}

export async function clearTaskDropdownsCache(
  signal?: AbortSignal,
): Promise<{ cleared: boolean }> {
  const response = await fetch(`${API_BASE_URL}/task-dropdowns/cache/clear`, {
    method: 'POST',
    signal,
  });
  if (!response.ok) {
    throw new Error(`Failed to clear task dropdowns cache: ${response.status}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Per-row CRUD (used by the modal editor + admin table)
// ---------------------------------------------------------------------------

export async function createTaskDropdown(
  payload: {
    dropdownId: string;
    displayName: string;
    description?: string;
    options: string[];
  },
  signal?: AbortSignal,
): Promise<TaskDropdownRow> {
  const response = await fetch(`${API_BASE_URL}/task-dropdowns`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    throw await _toError(response, 'create task dropdown');
  }
  return response.json();
}

export async function updateTaskDropdown(
  id: string,
  patch: { displayName?: string; description?: string | null; options?: string[] },
  signal?: AbortSignal,
): Promise<TaskDropdownRow> {
  const response = await fetch(
    `${API_BASE_URL}/task-dropdowns/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(patch),
      signal,
    },
  );
  if (!response.ok) {
    throw await _toError(response, 'update task dropdown');
  }
  return response.json();
}

export async function deleteTaskDropdown(
  id: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/task-dropdowns/${encodeURIComponent(id)}`,
    { method: 'DELETE', signal },
  );
  if (!response.ok) {
    throw await _toError(response, 'delete task dropdown');
  }
}

async function _toError(response: Response, action: string): Promise<Error> {
  let message = `Failed to ${action}: ${response.status}`;
  try {
    const body = await response.json();
    if (body && typeof body.error === 'string') {
      message = body.error;
    }
  } catch {
    // ignore
  }
  return new Error(message);
}
